import { Image } from 'antd'
import type { CSSProperties } from 'react'

const IMAGE_EXTENSIONS = /\.(apng|avif|gif|jpe?g|png|svg|webp)(\?.*)?(#.*)?$/i
const IMAGE_PROXY_HOSTS = [/^encrypted-tbn\d*\.gstatic\.com$/i, /(^|\.)googleusercontent\.com$/i]
const DEFAULT_IMAGE_FALLBACK =
   'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2264%22%20height=%2264%22%20viewBox=%220%200%2064%2064%22%3E%3Crect%20width=%2264%22%20height=%2264%22%20fill=%22%23f5f5f5%22/%3E%3Cpath%20d=%22M18%2044l9-11%207%208%205-6%207%209H18zm4-24h20a4%204%200%20014%204v20a4%204%200%2001-4%204H22a4%204%200%2001-4-4V24a4%204%200%20014-4zm0%204v20h20V24H22z%22%20fill=%22%23bfbfbf%22/%3E%3C/svg%3E'

interface ImagePreviewProps {
   src: string
   size?: ImagePreviewSize
   fit?: 'cover' | 'contain'
   wrapperClassName?: string
}

type ImagePreviewDimension = number | string
type ImagePreviewSize =
   | ImagePreviewDimension
   | {
        width: ImagePreviewDimension
        height: ImagePreviewDimension
     }

export const isImageUrl = (value?: string) => {
   if (!value) return false
   const trimmedValue = value.trim()

   if (/^data:image\//i.test(trimmedValue)) return true
   if (!/^https?:\/\//i.test(trimmedValue)) return false
   if (IMAGE_EXTENSIONS.test(trimmedValue)) return true

   try {
      const url = new URL(trimmedValue)
      return IMAGE_PROXY_HOSTS.some((hostPattern) => hostPattern.test(url.hostname))
   } catch (error) {
      return false
   }
}

export const getImageUrl = (path?: string, baseUrl?: string) => {
   if (!path) return ''

   const trimmedPath = path.trim()
   if (/^(https?:\/\/|data:image\/)/i.test(trimmedPath)) {
      return trimmedPath
   }

   return `${(baseUrl || '').replace(/\/$/, '')}/${trimmedPath.replace(/^\//, '')}`
}

const getPreviewSize = (size: ImagePreviewSize) => {
   if (typeof size === 'object') return size

   return {
      width: size,
      height: size
   }
}

const ImagePreview = ({ src, size = 64, fit = 'contain', wrapperClassName }: ImagePreviewProps) => {
   const { width, height } = getPreviewSize(size)
   const imageStyle: CSSProperties = {
      width: '100%',
      height: '100%',
      objectFit: fit,
      borderRadius: 6,
      cursor: 'pointer',
      display: 'block'
   }

   return (
      <Image
         width={width}
         height={height}
         wrapperClassName={wrapperClassName}
         wrapperStyle={{
            width,
            height,
            borderRadius: 6,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5'
         }}
         style={imageStyle}
         preview={{
            mask: null
         }}
         fallback={DEFAULT_IMAGE_FALLBACK}
         src={src}
      />
   )
}

export default ImagePreview
